import moment from "moment";
import {useEffect, useState, useCallback} from "react";
import {Link, useParams} from "react-router-dom";
import {Button, ListGroup, Placeholder} from "react-bootstrap";

import config from "@/assets/config.js";
import Util from "@/lib/Util.jsx";
import Alert from "@/components/ui/alert/alert.jsx";
import Title from "@/components/ui/title/title.jsx";
import FeedbackError from "@/components/ui/feedback-error/feedback-error.jsx";
import AnimatedComponents from "@/components/ui/animated-component/animated-components.jsx";
import PaginationWithItems from "@/components/pagination-with-items/pagination-with-items.jsx";

import bcAll from "@/components/breadcrumb-app/breadcrumb-context.jsx";
import {dateConfigs} from "@/assets/resources.js";
import apiClient from "@/assets/axios-config.js";
import {useHistoryData} from "@/hooks/use-history-data.jsx";

const useBreadcrumb = bcAll.useBreadcrumb;

moment.locale(dateConfigs.locale);

export default function HistoryDepartureTimes() {
  const {id} = useParams();
  const [dataAll, setDataAll] = useState([]);
  const {setLabel} = useBreadcrumb();
  const [currentPage, setCurrentPage] = useState(1);
  
  const fetchHistoryFn = useCallback(() => apiClient.get(`/history/lines/${id || "-1"}`), [id]);
  const {error, loaded, data, lineData} = useHistoryData(Util.checkIsValid(id) ? id : null, fetchHistoryFn);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  useEffect(() => {
    if (!Util.checkIsValid(id)) return;
  }, [id]);
  
  useEffect(() => {
    document.title = "Mobilidade - Histórico de horários";
    
    setLabel("history", "Histórico");
    setLabel("departure-times", "Horários de partida");
    
    if (lineData?.[0]) {
      const lineLabel = (`${lineData[0]["line_number"] || "Linha"} - ` + (lineData[0]["line_name"] ? lineData[0]["line_name"] : ""))?.replaceAll("/", " ⇄ ");
      setLabel(id, lineLabel);
    }
  }, [lineData, id]);
  
  useEffect(() => {
    if (data && Array.isArray(data) && data.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDataAll(
        data.map((item, index) => (
          <ListGroup.Item as={Link} className={index !== 0 && (index % 10) !== 0 ? "border-top-0" : "border-top"} to={`/history/departure-times/${id || 0}/${Util.renderText(moment(item?.["update_date"] ? `${item?.["update_date"]}` : moment.utc()).add(config.host.startsWith("http://localhost") ? 0 : -3, "h").format("YYYY[X]MM[X]DD"))}`} key={index}>
            <span className={"d-block text-primary"}>{Util.renderText(Util.diffToHuman(moment(item?.["update_date"] ? `${item?.["update_date"]}` : moment.utc()).add(config.host.startsWith("http://localhost") ? 0 : -3, "h")))}</span>
            <span className={"text-body-tertiary"}>{item?.["count_departure_times"]} horários atualizados</span>
          </ListGroup.Item>
        ))
      )
    }
  }, [data]);
  
  if (!Util.checkIsValid(id)) return <Alert variant={'danger'} margin={"mt-0"}>O id da linha não foi informado.</Alert>
  
  if (loaded) {
    return (
      <AnimatedComponents>
        <Placeholder as="h1" animation="glow"><Placeholder xs={6}/></Placeholder>
        <Placeholder as="h2" animation="glow"><Placeholder xs={4}/></Placeholder>
      </AnimatedComponents>
    )
  } else if (error) {
    console.error(error);
    return <FeedbackError code={error.response ? error.response.status || 500 : 500} text={error.message} type={'card'}/>;
  } else if (data.length === 0) {
    return <Alert variant={'danger'} margin={"mt-0"}>Esta linha não tem histórico de horários.</Alert>;
  } else {
    
    return (
      <AnimatedComponents>
        <h1 className={"m-0 p-0"}><span className={"text-body-secondary"}>Histórico de horários</span></h1>
        <Link to={`/lines/${id}`} className={"text-decoration-none"}>
          <Title type={"h2"} classX=" fs-3 d-inline text-body mt-1 p-0 d-block">
            <span className="" style={{fontSize: "inherit"}}>
              Linha {(lineData?.[0]?.["line_number"] + " - " + lineData?.[0]?.["departure_location"] + " ⇄ " + lineData?.[0]?.["destination_location"] || "")?.replaceAll("/", " ⇄ ")}
            </span>
          </Title>
        </Link>
        
        <section className={"d-flex flex-column gap-4 gap-sm-5 align-items-start mt-5"}>
          <AnimatedComponents>
            <ListGroup>
              {
                Array.isArray(dataAll) && dataAll.length && (
                  <PaginationWithItems items={dataAll} itemsPerPage={10} currentPage={currentPage} onPageChange={handlePageChange}/>
                )
              }
            </ListGroup>
          </AnimatedComponents>
        </section>
        
        <Link to={`/lines/${id}`} className={"d-inline-flex mt-3 text-decoration-none"}>
          <Button variant={"primary"}>Veja todas as informações da linha {lineData?.[0]?.["line_number"] ?? ""}</Button>
        </Link>
      </AnimatedComponents>
    );
  }
}
